import { useEffect, useState } from 'react';
import { authApi, portailApi } from '../../api/resources';
import { tr } from '../../lib/i18n';
import { portableValable } from '../../lib/portable';
import { C } from '../../theme/palette';
import { alerte, bouton, carte, champ, etiquette, ROLES } from './styles';

/**
 * « 👤 Mon compte » : qui est connecté, son numéro de portable — celui que le
 * livreur appelle sur le chantier —, la sécurité en plus s'il la veut, et la
 * déconnexion.
 *
 * @param {object} props propriétés
 * @param {object} props.profil la personne connectée
 * @param {(profil: object) => void} props.setProfil reprend le profil enregistré
 * @param {() => void} props.deconnexion ferme la session
 * @returns {import('react').ReactElement} la carte du compte
 */
export function MonCompte({ profil, setProfil, deconnexion }) {
  const [telephone, setTelephone] = useState(profil.telephone || '');
  const [etat, setEtat] = useState({ enCours: false, erreur: '', ok: false });
  const enregistre = async (e) => {
    e.preventDefault();
    if (!portableValable(telephone)) {
      setEtat({
        enCours: false,
        ok: false,
        erreur: 'Indiquez un numéro de portable valable : au moins 9 chiffres.',
      });
      return;
    }
    setEtat({ enCours: true, erreur: '', ok: false });
    try {
      const p = await portailApi.telephone(telephone.trim());
      if (p) setProfil(p);
      setEtat({ enCours: false, erreur: '', ok: true });
    } catch (err) {
      setEtat({
        enCours: false,
        ok: false,
        erreur: err?.message || 'Le numéro n’a pas pu être enregistré. Réessayez.',
      });
    }
  };
  return (
    <>
      <form style={carte} onSubmit={enregistre}>
        <h2 style={{ fontSize: 17, margin: '0 0 4px' }}>👤 Mon compte</h2>
        <div style={{ fontSize: 14, fontWeight: 800 }}>
          {[profil.prenom, profil.nom].filter(Boolean).join(' ')}
        </div>
        <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 12 }}>
          {profil.client}
          {ROLES[profil.role] ? ' · ' + tr(ROLES[profil.role]) : ''}
        </div>
        <label style={etiquette}>
          Téléphone mobile
          <input
            style={champ}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            maxLength={40}
            placeholder="079 123 45 67"
            value={telephone}
            onChange={(e) => {
              setTelephone(e.target.value);
              setEtat((x) => ({ ...x, ok: false }));
            }}
          />
        </label>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 6, lineHeight: 1.45 }}>
          Le livreur vous appelle sur ce numéro en arrivant sur le chantier.
        </div>
        {etat.erreur && (
          <div role="alert" style={alerte}>
            {etat.erreur}
          </div>
        )}
        {etat.ok && (
          <div
            role="status"
            style={{ color: C.green, fontWeight: 700, fontSize: 13, marginTop: 8 }}
          >
            ✓ Numéro enregistré
          </div>
        )}
        <button
          type="submit"
          disabled={etat.enCours}
          style={{ ...bouton(C.green), width: '100%', marginTop: 12 }}
        >
          {etat.enCours ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </form>
      <SecuriteCompte />
      <button style={{ ...bouton(C.navy, false), width: '100%' }} onClick={deconnexion}>
        Se déconnecter
      </button>
    </>
  );
}

/**
 * « 🔒 Sécuriser mon compte (facultatif) » : éteint au départ — l'adresse et
 * le mot de passe suffisent. Allumé, un premier code part par e-mail pour le
 * confirmer, puis un code est demandé à chaque connexion. L'éteindre demande
 * le mot de passe.
 *
 * @returns {import('react').ReactElement} la carte
 */
export function SecuriteCompte() {
  const [etat, setEtat] = useState(null);
  const [mode, setMode] = useState('repos');
  const [motDePasse, setMotDePasse] = useState('');
  const [code, setCode] = useState('');
  const [defi, setDefi] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    let annule = false;
    Promise.resolve()
      .then(() => authApi.etatDoubleAuth())
      .then((e) => !annule && setEtat(e || { active: false }))
      .catch(() => !annule && setEtat({ active: false }));
    return () => {
      annule = true;
    };
  }, []);
  const actif = !!etat?.active;
  const repos = () => {
    setMode('repos');
    setMotDePasse('');
    setCode('');
    setDefi(null);
    setErreur('');
  };
  const agit = async (appel, apres, refus) => {
    setEnCours(true);
    setErreur('');
    try {
      await apres(await appel());
    } catch {
      setErreur(tr(refus));
    } finally {
      setEnCours(false);
    }
  };
  const envoieLeCode = (e) => {
    e.preventDefault();
    agit(
      () => authApi.activeDoubleAuth(motDePasse, 'EMAIL'),
      (r) => {
        setDefi(r?.defi);
        setMotDePasse('');
        setMode('code');
      },
      'Le mot de passe ne correspond pas.',
    );
  };
  const confirme = (e) => {
    e.preventDefault();
    agit(
      () => authApi.confirmeDoubleAuth(defi, code.trim(), 'EMAIL'),
      () => {
        setEtat((x) => ({ ...x, active: true }));
        repos();
      },
      'Ce code ne convient pas. Réessayez.',
    );
  };
  const retire = (e) => {
    e.preventDefault();
    agit(
      () => authApi.desactiveDoubleAuth(motDePasse),
      () => {
        setEtat((x) => ({ ...x, active: false }));
        repos();
      },
      'Le mot de passe ne correspond pas.',
    );
  };
  return (
    <div style={{ ...carte, border: '2px solid ' + C.teal }}>
      <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>🔒 Sécuriser mon compte (facultatif)</h2>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          fontSize: 14,
        }}
      >
        <span>Demander aussi un code reçu par e-mail à chaque connexion</span>
        <input
          type="checkbox"
          checked={(actif && mode !== 'desactiver') || mode === 'activer' || mode === 'code'}
          disabled={etat === null || enCours || !!etat?.obligatoire}
          onChange={(e) => {
            setErreur('');
            if (e.target.checked && !actif) setMode('activer');
            else if (!e.target.checked && actif) setMode('desactiver');
            else repos();
          }}
          style={{ width: 22, height: 22, flexShrink: 0 }}
        />
      </label>
      <div style={{ fontSize: 12, color: C.grey, marginTop: 6, lineHeight: 1.5 }}>
        {actif
          ? tr('Activé : un code reçu par e-mail vous est demandé à chaque connexion.')
          : tr('Désactivé : votre e-mail et votre mot de passe suffisent pour vous connecter.')}
      </div>
      {(mode === 'activer' || mode === 'desactiver') && (
        <form onSubmit={mode === 'activer' ? envoieLeCode : retire} style={{ marginTop: 10 }}>
          <label style={etiquette}>
            Votre mot de passe
            <input
              style={champ}
              type="password"
              autoComplete="current-password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={repos} style={{ ...bouton(C.navy, false), flex: 1 }}>
              Annuler
            </button>
            <button
              type="submit"
              disabled={enCours || !motDePasse}
              style={{ ...bouton(mode === 'activer' ? C.green : C.red), flex: 1 }}
            >
              {mode === 'activer' ? tr('Envoyer le code') : tr('Retirer le code')}
            </button>
          </div>
        </form>
      )}
      {mode === 'code' && (
        <form onSubmit={confirme} style={{ marginTop: 10 }}>
          <div role="status" style={{ fontSize: 13, color: C.ink, marginBottom: 6 }}>
            Un code vient de partir à votre adresse e-mail : entrez-le ici.
          </div>
          <label style={etiquette}>
            Code reçu par e-mail
            <input
              style={champ}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button type="button" onClick={repos} style={{ ...bouton(C.navy, false), flex: 1 }}>
              Annuler
            </button>
            <button
              type="submit"
              disabled={enCours || code.length < 6}
              style={{ ...bouton(C.green), flex: 1 }}
            >
              Confirmer
            </button>
          </div>
        </form>
      )}
      {erreur && (
        <div role="alert" style={alerte}>
          {erreur}
        </div>
      )}
    </div>
  );
}
